/**
 * Códigos de receita federal (DARF) e de GPS mais usados pelo escritório, com o nome curto que dá título à guia
 * ("DARF 8741 — CIDE importação") e as obrigações do catálogo em que costumam aparecer. Compartilhado pela leitura
 * de PDF, pela emissão no SICALC, pelos títulos de documento e pela sugestão de tarefa.
 *
 * DCTFWeb e EFD-Reinf são "hubs": o que sai delas varia a cada mês (folha, serviços tomados, retenções, importações…),
 * por isso várias receitas apontam para DARF_DCTFWEB e EFD_REINF ao mesmo tempo.
 */
export type Tributo = 'IRRF' | 'IRPJ' | 'CSLL' | 'PIS' | 'COFINS' | 'CIDE' | 'CSRF' | 'INSS' | 'CPRB' | 'IRPF' | 'IPI' | 'OUTRO';
export type GuiaDaReceita = 'DARF' | 'GPS';

export interface CodigoReceita {
  codigo: string;
  /** nome curto, como vai no título da guia */
  curto: string;
  /** descrição oficial resumida */
  nome: string;
  tributo: Tributo;
  guia: GuiaDaReceita;
  /** obrigações do catálogo em que esta receita costuma nascer (a primeira é a mais provável) */
  obrigacoes: string[];
}

const HUB = ['DARF_DCTFWEB', 'EFD_REINF', 'DCTFWEB'];

export const CODIGOS_RECEITA: readonly CodigoReceita[] = [
  // ---- retenções na fonte (DCTFWeb / EFD-Reinf)
  { codigo: '0561', curto: 'IRRF folha', nome: 'IRRF — rendimentos do trabalho assalariado', tributo: 'IRRF', guia: 'DARF', obrigacoes: HUB },
  { codigo: '0588', curto: 'IRRF autônomos', nome: 'IRRF — rendimentos do trabalho sem vínculo empregatício', tributo: 'IRRF', guia: 'DARF', obrigacoes: HUB },
  { codigo: '1708', curto: 'IRRF serviços tomados', nome: 'IRRF — remuneração de serviços prestados por pessoa jurídica', tributo: 'IRRF', guia: 'DARF', obrigacoes: HUB },
  { codigo: '3208', curto: 'IRRF aluguéis', nome: 'IRRF — aluguéis e royalties pagos a pessoa física', tributo: 'IRRF', guia: 'DARF', obrigacoes: HUB },
  { codigo: '8045', curto: 'IRRF outros rendimentos', nome: 'IRRF — outros rendimentos (multas, juros, prêmios)', tributo: 'IRRF', guia: 'DARF', obrigacoes: HUB },
  { codigo: '5952', curto: 'CSRF serviços tomados', nome: 'Retenção de PIS, COFINS e CSLL — pagamentos a pessoa jurídica (art. 30 da Lei 10.833)', tributo: 'CSRF', guia: 'DARF', obrigacoes: HUB },
  { codigo: '5960', curto: 'COFINS retida', nome: 'COFINS — retenção na fonte', tributo: 'COFINS', guia: 'DARF', obrigacoes: HUB },
  { codigo: '5979', curto: 'CSLL retida', nome: 'CSLL — retenção na fonte', tributo: 'CSLL', guia: 'DARF', obrigacoes: HUB },
  { codigo: '5987', curto: 'PIS retido', nome: 'PIS/Pasep — retenção na fonte', tributo: 'PIS', guia: 'DARF', obrigacoes: HUB },
  // ---- importação de serviços (remessas ao exterior)
  { codigo: '0422', curto: 'IRRF remessa exterior (royalties/assistência técnica)', nome: 'IRRF — royalties e pagamentos de assistência técnica a residentes no exterior', tributo: 'IRRF', guia: 'DARF', obrigacoes: HUB },
  { codigo: '0473', curto: 'IRRF remessa exterior (serviços)', nome: 'IRRF — rendimentos de residentes ou domiciliados no exterior', tributo: 'IRRF', guia: 'DARF', obrigacoes: HUB },
  { codigo: '0481', curto: 'IRRF juros ao exterior', nome: 'IRRF — juros e comissões remetidos ao exterior', tributo: 'IRRF', guia: 'DARF', obrigacoes: HUB },
  { codigo: '8741', curto: 'CIDE importação', nome: 'CIDE — remessas ao exterior (serviços técnicos, royalties)', tributo: 'CIDE', guia: 'DARF', obrigacoes: HUB },
  { codigo: '5434', curto: 'PIS importação', nome: 'PIS/Pasep-Importação — serviços', tributo: 'PIS', guia: 'DARF', obrigacoes: HUB },
  { codigo: '5629', curto: 'COFINS importação', nome: 'COFINS-Importação — serviços', tributo: 'COFINS', guia: 'DARF', obrigacoes: HUB },
  // ---- PIS e COFINS sobre o faturamento
  { codigo: '8109', curto: 'PIS faturamento', nome: 'PIS/Pasep — faturamento (regime cumulativo)', tributo: 'PIS', guia: 'DARF', obrigacoes: ['PIS_COFINS'] },
  { codigo: '6912', curto: 'PIS não cumulativo', nome: 'PIS/Pasep — regime não cumulativo', tributo: 'PIS', guia: 'DARF', obrigacoes: ['PIS_COFINS'] },
  { codigo: '8301', curto: 'PIS folha', nome: 'PIS/Pasep — folha de salários (entidades sem fins lucrativos)', tributo: 'PIS', guia: 'DARF', obrigacoes: ['PIS_COFINS'] },
  { codigo: '2172', curto: 'COFINS faturamento', nome: 'COFINS — faturamento (regime cumulativo)', tributo: 'COFINS', guia: 'DARF', obrigacoes: ['PIS_COFINS'] },
  { codigo: '5856', curto: 'COFINS não cumulativa', nome: 'COFINS — regime não cumulativo', tributo: 'COFINS', guia: 'DARF', obrigacoes: ['PIS_COFINS'] },
  // ---- IRPJ e CSLL
  { codigo: '2089', curto: 'IRPJ presumido', nome: 'IRPJ — lucro presumido', tributo: 'IRPJ', guia: 'DARF', obrigacoes: ['IRPJ_CSLL_TRIM'] },
  { codigo: '2372', curto: 'CSLL presumido', nome: 'CSLL — lucro presumido', tributo: 'CSLL', guia: 'DARF', obrigacoes: ['IRPJ_CSLL_TRIM'] },
  { codigo: '0220', curto: 'IRPJ real trimestral', nome: 'IRPJ — lucro real, apuração trimestral', tributo: 'IRPJ', guia: 'DARF', obrigacoes: ['IRPJ_CSLL_TRIM'] },
  { codigo: '6012', curto: 'CSLL real trimestral', nome: 'CSLL — lucro real, apuração trimestral', tributo: 'CSLL', guia: 'DARF', obrigacoes: ['IRPJ_CSLL_TRIM'] },
  { codigo: '2362', curto: 'IRPJ estimativa', nome: 'IRPJ — lucro real, estimativa mensal', tributo: 'IRPJ', guia: 'DARF', obrigacoes: ['IRPJ_CSLL_EST'] },
  { codigo: '2484', curto: 'CSLL estimativa', nome: 'CSLL — lucro real, estimativa mensal', tributo: 'CSLL', guia: 'DARF', obrigacoes: ['IRPJ_CSLL_EST'] },
  // ---- previdência
  { codigo: '2985', curto: 'CPRB comércio/indústria', nome: 'Contribuição previdenciária sobre a receita bruta — art. 7º da Lei 12.546', tributo: 'CPRB', guia: 'DARF', obrigacoes: HUB },
  { codigo: '2991', curto: 'CPRB serviços', nome: 'Contribuição previdenciária sobre a receita bruta — art. 8º da Lei 12.546', tributo: 'CPRB', guia: 'DARF', obrigacoes: HUB },
  { codigo: '2100', curto: 'INSS empresa (GPS)', nome: 'GPS — empresas em geral', tributo: 'INSS', guia: 'GPS', obrigacoes: ['DARF_DCTFWEB', 'ESOCIAL_FOLHA'] },
  { codigo: '1007', curto: 'INSS contribuinte individual (GPS)', nome: 'GPS — contribuinte individual, recolhimento mensal', tributo: 'INSS', guia: 'GPS', obrigacoes: ['ESOCIAL_FOLHA'] },
  // ---- pessoa física
  { codigo: '0190', curto: 'Carnê-leão', nome: 'IRPF — carnê-leão (recolhimento mensal)', tributo: 'IRPF', guia: 'DARF', obrigacoes: ['DIRPF'] },
  { codigo: '0211', curto: 'IRPF quota', nome: 'IRPF — quotas do ajuste anual', tributo: 'IRPF', guia: 'DARF', obrigacoes: ['DIRPF'] },
];

const porCodigo = new Map(CODIGOS_RECEITA.map((c) => [c.codigo, c]));
/** "5952" ou "5952-01" — nada de "DARF 1162" virando título */
export const CODIGO_RECEITA_RE = /^\d{4}(-\d{2})?$/;
const JA_E_GUIA = /^(DAS|DARF|GPS|GNRE|GARE|DAE|DAM|GRF|GRRF)/i;

/** "5952" ou "5952-01" → o cadastro da receita (ou nulo). */
export function receitaDoCodigo(codigo: string | null | undefined): CodigoReceita | null {
  if (!codigo) return null;
  const c = codigo.trim();
  if (!CODIGO_RECEITA_RE.test(c)) return null;
  return porCodigo.get(c.slice(0, 4)) ?? null;
}

/** Tipo de guia que a obrigação do catálogo costuma gerar (para a guia registrada sem leitura do PDF). */
export function tipoGuiaDaObrigacao(codigoObrigacao: string | null | undefined): 'DAS' | 'DARF' | 'FGTS' | 'ISS' | 'ICMS' | null {
  switch (codigoObrigacao) {
    case 'DAS': case 'DAS_MEI': return 'DAS';
    case 'FGTS_DIGITAL': return 'FGTS';
    case 'ISS': return 'ISS';
    case 'ICMS': return 'ICMS';
    case 'DARF_DCTFWEB': case 'DCTFWEB': case 'EFD_REINF': case 'PIS_COFINS': case 'IRPJ_CSLL_TRIM': case 'IRPJ_CSLL_EST': return 'DARF';
    default: return null;
  }
}

/** Obrigações candidatas para uma guia pelo código de receita (vazio = desconhecido). */
export function obrigacoesDaReceita(codigo: string | null | undefined): string[] {
  return receitaDoCodigo(codigo)?.obrigacoes ?? [];
}

/**
 * Título da guia como o cliente vê: "DARF 8741 — CIDE importação", "GPS 2100 — INSS empresa", "DAS", "GUIA DE FGTS".
 * O código entra quando existe; o nome curto só quando o código é conhecido. Competência não entra aqui (vai ao lado, no e-mail e no portal).
 */
export function tituloDaGuia(tipo: string | null | undefined, codigo: string | null | undefined, siglaObrigacao?: string | null): string {
  const t = (tipo ?? '').toUpperCase();
  const r = receitaDoCodigo(codigo);
  const limpo = codigo?.trim();
  const cod = limpo && CODIGO_RECEITA_RE.test(limpo) ? limpo.slice(0, 4) : undefined;
  if (t === 'DARF' || t === 'GPS' || (!t && r)) {
    const base = t || r!.guia;
    if (r) return `${base} ${r.codigo} — ${r.curto}`;
    if (cod) return `${base} ${cod}`;
    return siglaObrigacao ? `${base} — ${siglaObrigacao}` : base;
  }
  if (t === 'DAS') return 'DAS';
  if (t === 'FGTS') return 'GUIA DE FGTS';
  if (t === 'DAE') return 'DAE';
  if (t === 'ISS') return 'GUIA DE ISS';
  if (t === 'ICMS') return 'GUIA DE ICMS';
  if (t === 'GNRE') return 'GNRE';
  if (t) return `GUIA DE ${t}`;
  if (!siglaObrigacao) return 'GUIA';
  return JA_E_GUIA.test(siglaObrigacao) ? siglaObrigacao.toUpperCase() : `GUIA DE ${siglaObrigacao.toUpperCase()}`;
}
